/**
 * @ward3/frontend-shared/hooks/useAuthEvents
 *
 * Hook React qui câble les 2 side-effects partagés entre les 2
 * AuthContext (SOC + admin) :
 *
 *   1. **Listener `anm:auth-expired`** dispatché par l'interceptor
 *      axios quand le silent refresh échoue. Chaque app doit reset son
 *      state (user, access token, éventuel scope override) — le
 *      callback `onExpired` reçoit cette responsabilité, le hook n'a
 *      pas de state global à supposer.
 *
 *   2. **Sync i18n** : quand `user.locale` change (via `/auth/me` ou
 *      `updateLocale`), on appelle `i18n.changeLanguage(...)` si la
 *      langue effective diffère.
 *
 * Extraction 2026-07-06 (#17-A revue 2026-06). Le AuthContext de
 * chaque app reste maître de sa state — le hook est juste le socle
 * commun des side-effects.
 */

import { useEffect } from "react";

/**
 * @param {object} params
 * @param {object} params.i18n            Instance i18next (l'export default
 *                                        de chaque `src/i18n.js` app-side).
 * @param {string|null|undefined} params.locale
 *                                        La locale courante lue depuis le user
 *                                        state ; `null`/`undefined` = pas connue
 *                                        (login screen anonyme), on ne touche
 *                                        pas à i18n dans ce cas.
 * @param {() => void} params.onExpired   Callback appelé quand
 *                                        `anm:auth-expired` fire — chaque app
 *                                        y fait son reset (setUser(null),
 *                                        setAccessToken(null), + éventuel scope
 *                                        override côté SOC).
 */
export function useAuthEvents({ i18n, locale, onExpired }) {
  // Sync i18n dès qu'on connaît la locale user.
  useEffect(() => {
    if (locale && i18n?.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  // Handler `anm:auth-expired`.
  useEffect(() => {
    const handler = () => onExpired?.();
    window.addEventListener("anm:auth-expired", handler);
    return () => window.removeEventListener("anm:auth-expired", handler);
  }, [onExpired]);
}
